import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Copy, Trash2, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';
import { useSEO } from '../hooks/useSEO';

const CharacterCounter: React.FC = () => {
  const [text, setText] = useState('');
  const [stats, setStats] = useState({
    totalCharacters: 0,
    charactersNoSpaces: 0,
    alphabetic: 0,
    numeric: 0,
    symbols: 0,
    spaces: 0,
    lines: 0
  });

  // SEO optimization
  useSEO({
    title: 'Character Counter - Detailed Text Analysis | SnapTools',
    description: 'Free character counter with detailed analysis. Count alphabetic, numeric characters, symbols, spaces and lines. Real-time character analysis tool.',
    keywords: 'character counter, text analysis, alphabetic counter, numeric counter, symbol counter, text statistics, character analysis'
  });

  useEffect(() => {
    const calculateStats = () => {
      const totalCharacters = text.length;
      const charactersNoSpaces = text.replace(/\s/g, '').length;
      const alphabetic = (text.match(/[a-zA-Z]/g) || []).length;
      const numeric = (text.match(/[0-9]/g) || []).length;
      const symbols = (text.match(/[^a-zA-Z0-9\s]/g) || []).length;
      const spaces = (text.match(/\s/g) || []).length;
      const lines = text ? text.split('\n').length : 0;

      setStats({
        totalCharacters,
        charactersNoSpaces,
        alphabetic,
        numeric,
        symbols,
        spaces,
        lines
      });
    };

    calculateStats();
  }, [text]);

  const clearText = () => {
    setText('');
    toast.success('Text cleared successfully');
  };

  const copyText = () => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Text copied to clipboard');
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-12">
      <div className="max-w-5xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6">
            <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4 px-2">
            Character Counter
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Detailed character analysis and counting for your text. Analyze alphabetic, numeric characters, symbols and more.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Input Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-[#8438ff]/10 rounded-xl flex items-center justify-center">
                    <FileText className="h-5 w-5 text-[#8438ff]" />
                  </div>
                  <h2 className="text-xl font-bold text-[#8438ff]">Enter Your Text</h2>
                </div>
                <div className="flex space-x-2">
                  {text && (
                    <>
                      <button
                        onClick={copyText}
                        className="p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                        title="Copy text"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={clearText}
                        className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Clear text"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type or paste your text here for detailed character analysis..."
                className="w-full h-80 p-4 border-2 border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
              />
              
              <div className="mt-4 text-sm text-gray-500">
                {text.length > 0 && (
                  <span>Live counting: {stats.totalCharacters} total characters, {stats.alphabetic} alphabetic</span>
                )}
              </div>
            </motion.div>
          </div>

          {/* Statistics Panel */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8 sticky top-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-[#8438ff]/10 rounded-xl flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-[#8438ff]" />
                </div>
                <h2 className="text-xl font-bold text-[#8438ff]">Character Analysis</h2>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-xl">
                  <div className="text-3xl font-bold text-blue-600">{stats.totalCharacters}</div>
                  <div className="text-sm text-gray-600">Total Characters</div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-xl">
                  <div className="text-3xl font-bold text-purple-600">{stats.charactersNoSpaces}</div>
                  <div className="text-sm text-gray-600">Characters (no spaces)</div>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl">
                  <div className="text-3xl font-bold text-green-600">{stats.alphabetic}</div>
                  <div className="text-sm text-gray-600">Alphabetic Characters</div>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-xl">
                  <div className="text-3xl font-bold text-yellow-600">{stats.numeric}</div>
                  <div className="text-sm text-gray-600">Numeric Characters</div>
                </div>
                
                <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-xl">
                  <div className="text-3xl font-bold text-red-600">{stats.symbols}</div>
                  <div className="text-sm text-gray-600">Symbols & Punctuation</div>
                </div>
                
                <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-xl">
                  <div className="text-3xl font-bold text-gray-600">{stats.spaces}</div>
                  <div className="text-sm text-gray-600">Spaces</div>
                </div>
                
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl">
                  <div className="text-3xl font-bold text-indigo-600">{stats.lines}</div>
                  <div className="text-sm text-gray-600">Lines</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-12"
        />
      </div>
    </div>
  );
};

export default CharacterCounter;
